import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ListenButton from "../../components/ListenButton";
import GameShell, { shuffle } from "./GameShell";

export default function WordBuildGame() {
    const [data, setData] = useState([]);
    const [idx, setIdx] = useState(0);
    const [placed, setPlaced] = useState([]);
    const [bank, setBank] = useState([]);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/games/word-builder").then((r) => {
            setData(r.data);
            setupRound(r.data, 0);
        });
    }, []);

    const setupRound = (arr, i) => {
        const item = arr[i];
        setPlaced([]);
        setBank(shuffle(item.letters));
    };

    const pickLetter = (letter, i) => {
        setPlaced((p) => [...p, letter]);
        setBank((b) => b.filter((_, idx2) => idx2 !== i));
    };

    const removePlaced = (i) => {
        const letter = placed[i];
        setPlaced((p) => p.filter((_, idx2) => idx2 !== i));
        setBank((b) => [...b, letter]);
    };

    const check = async () => {
        const guess = placed.join("");
        const target = data[idx].word;
        if (guess.toLowerCase() === target.toLowerCase()) {
            const newScore = score + 1;
            setScore(newScore);
            toast.success(`Correct! ${target}`);
            if (idx + 1 >= data.length) {
                setDone(true);
                try {
                    await api.post("/progress/game-score", { game: "word-build", score: newScore * 8, correct: newScore, total: data.length });
                    toast.success(`+${newScore * 8} XP earned`);
                } catch {}
            } else {
                const next = idx + 1;
                setTimeout(() => { setIdx(next); setupRound(data, next); }, 700);
            }
        } else {
            toast.error("Not quite — try again");
        }
    };

    const reset = () => {
        setScore(0); setIdx(0); setDone(false); setupRound(data, 0);
    };

    if (!data.length) return <GameShell title="Word Builder"><p>Loading…</p></GameShell>;

    const current = data[idx];

    return (
        <GameShell
            title="Word Builder"
            intro="Tap letters to spell the word you hear."
            testId="word-build-game"
            instructions="This is the Word Builder game. Tap Hear the word to listen to a whole word. Tap Sound it out to hear each sound separately. Then tap the letters below in order to spell the word you heard. Tap a placed letter to remove it."
        >
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#819888]">Word {idx + 1} of {data.length}</p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="wb-score">Correct: {score}</p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>All done!</h2>
                    <p className="text-lg text-[#515B57] mb-6">You spelled {score} of {data.length} words correctly.</p>
                    <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium" data-testid="wb-replay">
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    <div className="card-rw p-10 mb-6 text-center">
                        <p className="label-eyebrow mb-3">Listen and spell</p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <ListenButton text={current.word} size="lg" label="Hear the word" testId="wb-listen" />
                            <ListenButton
                                text={current.word}
                                mode="sound_out"
                                size="lg"
                                label="Sound it out"
                                testId="wb-sound-out"
                            />
                        </div>
                    </div>

                    <div className="card-rw p-6 mb-6 min-h-[100px] flex items-center justify-center flex-wrap gap-3" data-testid="wb-placed">
                        {placed.length === 0 ? (
                            <p className="text-[#819888]">Tap letters below to build the word</p>
                        ) : (
                            placed.map((l, i) => (
                                <button
                                    key={i}
                                    onClick={() => removePlaced(i)}
                                    className="tile tile-correct w-14 h-14 text-2xl"
                                    data-testid={`wb-placed-${i}`}
                                >
                                    {l}
                                </button>
                            ))
                        )}
                    </div>

                    <div className="flex flex-wrap gap-3 justify-center mb-6" data-testid="wb-bank">
                        {bank.map((l, i) => (
                            <button
                                key={i}
                                onClick={() => pickLetter(l, i)}
                                className="tile w-14 h-14 text-2xl"
                                data-testid={`wb-bank-${i}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={check}
                        disabled={placed.length === 0}
                        className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] disabled:opacity-50"
                        data-testid="wb-check"
                    >
                        Check
                    </button>
                </>
            )}
        </GameShell>
    );
}
